import React, { useEffect, useMemo } from 'react'
import { LuCircleAlert } from 'react-icons/lu'

import Select from '@/components/select/Select'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useReferralServiceAreaOptions } from '@/hooks/useReferralServiceAreaOptions'
import { useServiceAreas } from '@/hooks/useServiceAreas'
import { REFERRAL_IMPORT_DIRECTION, SERVICE_AREA_ID } from '@/lib/constants'
import { cn } from '@/lib/utils'
import {
  selectIsLoggingDirectLeadReferral,
  selectLogHistoricalReferralState,
  updateLogData,
} from '@/redux/slices/logHistoricalReferral'

interface Props {
  className?: string
  disabled?: boolean
}

const SelectServiceArea: React.FC<Props> = ({
  className,
  disabled = false,
}) => {
  const dispatch = useAppDispatch()
  const referralLogHistoricalState = useAppSelector(
    selectLogHistoricalReferralState,
  )
  const interceptorGuessedServiceAreaIds =
    referralLogHistoricalState.logData.interceptorGuessedServiceAreaIds
  const referralDirection = referralLogHistoricalState.logData.referralDirection

  const isLoggingDirectLeadReferral = useAppSelector(
    selectIsLoggingDirectLeadReferral,
  )

  const serviceCompany = useMemo(() => {
    return referralDirection === REFERRAL_IMPORT_DIRECTION.TO
      ? referralLogHistoricalState.logData.otherCompany
      : referralLogHistoricalState.selectedUser?.company
  }, [
    referralDirection,
    referralLogHistoricalState.logData,
    referralLogHistoricalState.selectedUser?.company,
  ])

  const { serviceAreaOptions } = useReferralServiceAreaOptions({
    company: serviceCompany || undefined,
    includeGeneralEnquiry: isLoggingDirectLeadReferral,
  })

  const generalEnquiryServiceOption = serviceAreaOptions.find(
    (serviceAreaOption) =>
      +serviceAreaOption.value === SERVICE_AREA_ID.generalEnquiry,
  )

  const allServiceAreas = useServiceAreas()

  const serviceAreaId = referralLogHistoricalState.logData.serviceAreaId

  const selectedServiceAreaOption = serviceAreaOptions.find(
    (option) => option.value.id == serviceAreaId,
  )

  useEffect(() => {
    // When used as part of the interceptor referral logging we need to check the guessed service area ids exist and then set automatically to the first available
    if (!serviceCompany?.id || !interceptorGuessedServiceAreaIds?.length) {
      return
    }

    if (!serviceAreaOptions.length || serviceAreaId !== -1) {
      return
    }

    const firstMatchingOption = serviceAreaOptions.find((o) =>
      interceptorGuessedServiceAreaIds.some((id) => id === o.value.id),
    )

    if (!firstMatchingOption) {
      dispatch(updateLogData({ serviceAreaId: -1 }))
      return
    }

    dispatch(updateLogData({ serviceAreaId: firstMatchingOption.value.id }))
  }, [
    dispatch,
    interceptorGuessedServiceAreaIds,
    serviceAreaId,
    serviceAreaOptions,
    serviceCompany?.id,
  ])

  return (
    <div className={cn('relative', className)}>
      <Select
        key={selectedServiceAreaOption?.value?.id}
        isDisabled={disabled}
        placeholder="Select service..."
        defaultValue={generalEnquiryServiceOption}
        options={serviceAreaOptions}
        onChange={(selectedOption) => {
          const selectedServiceArea =
            selectedOption &&
            allServiceAreas.find(
              (serviceArea) => serviceArea.id === selectedOption.value.id,
            )

          dispatch(
            updateLogData({
              serviceAreaId: selectedOption?.value.id || -1,
              newServiceAreaId:
                selectedOption?.isNew &&
                !selectedServiceArea?.isFcaServiceArea &&
                referralDirection === REFERRAL_IMPORT_DIRECTION.FROM
                  ? selectedOption?.value.id
                  : null,
            }),
          )
        }}
        value={selectedServiceAreaOption}
      />

      {referralLogHistoricalState.logData.newServiceAreaId && (
        <Tooltip>
          <div className="flex text-dim mt-2 text-xs gap-2">
            <TooltipTrigger asChild>
              <LuCircleAlert className="ml-1 size-4" />
            </TooltipTrigger>
            Selected service will be added to your company
          </div>

          <TooltipContent>
            Selected service is not yet added in your services. It will be added
            automatically upon creation of this referral log.
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  )
}

export default SelectServiceArea
